import { Component, EventEmitter, OnInit, Output, ViewChild, Input } from '@angular/core';
import { NgForm } from '@angular/forms';
import { DrFilter } from '../../../models/dr-filter';
import { Endpoint, FilterAttribute, FilterOperator } from '../../../models/enums';
import { TaskStatusTitle } from '../../../models/task';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import * as moment from 'moment';
import { cloneDeep } from 'lodash';

@Component({
    standalone: false,
    selector: 'res-task-filter',
    templateUrl: './task-filter.component.html',
    styleUrls: ['./task-filter.component.scss']
})

export class TaskFilterComponent implements OnInit {
    @Output() onApplyFilters: EventEmitter<any> = new EventEmitter();
    @ViewChild('taskFiltersForm', { static: true }) taskFiltersForm: NgForm;

    selectedFilters: Array<DrFilter>;
    statusList: string[];
    ownersList: string[];
    private _isOwnerVisible: boolean;
    isFormSubmitted: boolean;
    defaultDatePlaceholders = ['On or after ...', 'On or before ...'];
    @Input() automationId = '';
    filterObject = null;
    filterObjectToRestore = null;
    selectedFiltersToRestore: Array<DrFilter>;

    // Owner should be visible only when all users tasks is checked in WB, visibility is set from WB
    get isOwnerVisible(): boolean {
        return this._isOwnerVisible;
    }

    set isOwnerVisible(value: boolean) {
        this._isOwnerVisible = value;
    }
    constructor(private restService: RestService,
        private commonService: CommonService) { }

    ngOnInit(): void {
        this.initFilterObject();
        this.selectedFilters = new Array<DrFilter>();
        this.statusList = this.commonService.getEnumNames(TaskStatusTitle);
        this.statusList.sort();
        this.statusList.unshift('All');
        this.filterObject.selectedTaskStatus.value = this.statusList[0];
        if (this.commonService.currentProject) {
            this.loadUsersList();
        }
    }

    initFilterObject(): void {
        this.filterObject = {
            maxResult: { value: '', filterApplied: false, filteredValue: '' },
            minResult: { value: '', filterApplied: false, filteredValue: '' },
            taskDescription: { value: '', filterApplied: false, filteredValue: '' },
            selectedOwner: { value: 'All', filterApplied: false, filteredValue: '' },
            selectedTaskStatus: { value: 'All', filterApplied: false, filteredValue: '' },
            dateStartedAfter: { value: null, filterApplied: false, filteredValue: null, operator: FilterOperator.AFTER },
            dateStartedBefore: { value: null, filterApplied: false, filteredValue: null, operator: FilterOperator.BEFORE },
            dateFinnishedBefore: { value: null, filterApplied: false, filteredValue: null, operator: FilterOperator.BEFORE },
            dateFinnishedAfter: { value: null, filterApplied: false, filteredValue: null, operator: FilterOperator.AFTER },
        }
    }

    loadUsersList(): void {
        this.restService.post(Endpoint.projectManager_listWorkbasketUsers, { handle: this.commonService.currentProject.handle }).then(response => {
            if (response && response.entityNames) {
                this.ownersList = response.entityNames ? response.entityNames.filter(e => e !== null) : response.entityNames;
                this.ownersList.sort();
                this.ownersList.unshift('All');
                this.filterObject.selectedOwner.value = this.ownersList[0];
                if (this.filterObjectToRestore) {
                    this.filterObject = cloneDeep(this.filterObjectToRestore);
                    this.restoreDateValuesFromObject();
                    this.filterObjectToRestore = null;
                    if (!this.filterObject.selectedTaskStatus.value) {
                        this.filterObject.selectedTaskStatus.value = this.statusList[0];
                    }
                }
                if (this.selectedFiltersToRestore) {
                    this.selectedFilters = cloneDeep(this.selectedFiltersToRestore);
                    this.selectedFiltersToRestore = null;
                    this.isFormSubmitted = true;
                }
            }
        });
    }

    restoreDateValuesFromObject(): void {
        if (this.filterObject) {
            const props = Object.keys(this.filterObject);
            props.forEach(p => {
                if (this.filterObject[p].value && this.filterObject[p].operator) {
                    const timeString = this.filterObject[p].operator === FilterOperator.AFTER ? 'T00:00:00' : 'T23:59:59';
                    this.filterObject[p].value = new Date(moment(this.filterObject[p].value).format('YYYY-MM-DD') + timeString);
                    if (this.filterObject[p].filteredValue) {
                        this.filterObject[p].filteredValue = this.filterObject[p].value;
                    }
                }
            });
        }
    }

    applyFilters(): void {
        this.setSelectedFilterList();
        this.onApplyFilters.emit(this.selectedFilters);
        this.isFormSubmitted = true;
        this.updateFilterAppliedFlagForFilterObject(this.isFormSubmitted);
    }

    updateFilterAppliedFlagForFilterObject(isFilterApplied: boolean): void {
        if (this.filterObject) {
            const props = Object.keys(this.filterObject);
            props.forEach(p => {
                if (isFilterApplied && (this.filterObject[p].value !== '' && this.filterObject[p].value !== 'All' && this.filterObject[p].value !== null)) {
                    this.filterObject[p].filterApplied = isFilterApplied;
                }
                this.filterObject[p].filteredValue = this.filterObject[p].value;
            });
        }
    }

    setSelectedFilterList(): void {
        this.selectedFilters = [];
        if (this.filterObject.taskDescription.value) {
            this.selectedFilters.push(new DrFilter({ attribute: FilterAttribute.DESCRIPTION, operator: FilterOperator.EQUALS, value: this.filterObject.taskDescription.value }));
        }
        if (this.filterObject.dateStartedAfter.value) {
            this.filterObject.dateStartedAfter.value.setHours(0, 0, 0);
            this.selectedFilters.push(new DrFilter({ attribute: FilterAttribute.STARTTIME, operator: FilterOperator.AFTER, value: this.filterObject.dateStartedAfter.value.valueOf() }));
        }
        if (this.filterObject.dateStartedBefore.value) {
            this.filterObject.dateStartedBefore.value.setHours(23, 59, 59);
            this.selectedFilters.push(new DrFilter({ attribute: FilterAttribute.STARTTIME, operator: FilterOperator.BEFORE, value: this.filterObject.dateStartedBefore.value.valueOf() }));
        }
        if (this.filterObject.selectedTaskStatus?.value !== 'All') {
            const keys = Object.keys(TaskStatusTitle).filter(x => TaskStatusTitle[x] === this.filterObject.selectedTaskStatus.value);
            if (keys.length > 0) {
                this.selectedFilters.push(new DrFilter({ attribute: FilterAttribute.STATUS, operator: FilterOperator.EQUALS, value: keys[0] }));
            }
        }
        if (this.filterObject.dateFinnishedAfter.value) {
            this.filterObject.dateFinnishedAfter.value.setHours(0, 0, 0);
            this.selectedFilters.push(new DrFilter({ attribute: FilterAttribute.COMPLETETIME, operator: FilterOperator.AFTER, value: this.filterObject.dateFinnishedAfter.value.valueOf() }));
        }
        if (this.filterObject.dateFinnishedBefore.value) {
            this.filterObject.dateFinnishedBefore.value.setHours(23, 59, 59);
            this.selectedFilters.push(new DrFilter({ attribute: FilterAttribute.COMPLETETIME, operator: FilterOperator.BEFORE, value: this.filterObject.dateFinnishedBefore.value.valueOf() }));
        }
        if (this.filterObject.maxResult.value) {
            const nrMaxResult = parseInt(this.filterObject.maxResult.value);
            this.selectedFilters.push(new DrFilter({ attribute: FilterAttribute.WORKBASKET_COUNT, operator: FilterOperator.LESS_THAN, value: (nrMaxResult + 1) }));
        }
        if (this.filterObject.minResult.value) {
            const nrMinResult = parseInt(this.filterObject.minResult.value);
            if (nrMinResult > 0) {
                this.selectedFilters.push(new DrFilter({ attribute: FilterAttribute.WORKBASKET_COUNT, operator: FilterOperator.GREATER_THAN, value: (nrMinResult - 1) }));
            }
        }
        if (this.filterObject.selectedOwner?.value !== null && this.filterObject.selectedOwner?.value !== 'All') {
            this.selectedFilters.push(new DrFilter({ attribute: FilterAttribute.OWNER, operator: FilterOperator.EQUALS, value: this.filterObject.selectedOwner.value }));
        }
    }

    clearFilters(): void {
        this.taskFiltersForm.reset();
        this.initFilterObject();
        this.isFormSubmitted = false;
        if (this.selectedFilters && this.selectedFilters.length > 0) {
            this.selectedFilters = [];
            this.onApplyFilters.emit(this.selectedFilters);
        }
    }

    // if date picker input was clicked set input placeholder to YYYY-MM-DD
    // if x button or x button icon was clicked reset input placeholder to default
    onDateClick(datePicker: any, mode: any): void {
        if (datePicker && datePicker.target) {
            const crtControl = datePicker.target;
            const defaultPlaceholder = mode === 'after' ? this.defaultDatePlaceholders[0] : this.defaultDatePlaceholders[1];
            if (crtControl.placeholder) {
                if (this.defaultDatePlaceholders.indexOf(crtControl.placeholder) !== -1) {
                    crtControl.placeholder = 'YYYY-MM-DD';
                } else {
                    crtControl.placeholder = defaultPlaceholder;
                }
            } else if (crtControl.id === 'pickerClearBtn') {   // x button was clicked
                crtControl.parentElement.children['datepickerinput'].placeholder = defaultPlaceholder;
            } else if (crtControl.parentElement && crtControl.parentElement.id === 'pickerClearBtn') {  // x button icon was clicked
                crtControl.parentElement.parentElement.children['datepickerinput'].placeholder = defaultPlaceholder;
            }
        }
    }

    // on date picker input lost focus and date was not changed reset placeholder to default
    onFocusLost(datePicker: any, mode: any): void {
        if (datePicker && datePicker.target) {
            const crtControl = datePicker.target;
            const defaultPlaceholder = mode === 'after' ? this.defaultDatePlaceholders[0] : this.defaultDatePlaceholders[1];
            if (crtControl.placeholder) {
                if (this.defaultDatePlaceholders.indexOf(crtControl.placeholder) === -1) {
                    crtControl.placeholder = defaultPlaceholder;
                }
            }
        }
    }

    labelVisible(): boolean {
        const reportPanel = document.getElementById('FilterActionsID');
        if (reportPanel?.clientWidth) {
            return reportPanel.clientWidth >= 480;
        }
    }

    dateValueChanged(dateFieldName: string): void {
        this.onInputValueChanged(this.filterObject[dateFieldName]);
    }

    onInputValueChanged(filterItem?: any): void {
        if (this.isFormSubmitted) {
            const hasValueSet = filterItem.value !== '' && filterItem.value !== 'All' && filterItem.value !== null;
            const isDateField = filterItem.operator === FilterOperator.AFTER || filterItem.operator === FilterOperator.BEFORE;
            const originalValueRestored = isDateField ? filterItem.value?.getDate() === filterItem.filteredValue?.getDate() : filterItem.value === filterItem.filteredValue
            if (hasValueSet && originalValueRestored) {
                filterItem.filterApplied = true;
            } else {
                filterItem.filterApplied = false;
            }
        }
    }

    onSelectionChange(fieldName: string): void {
        this.onInputValueChanged(this.filterObject[fieldName]);
    }
}
